import { makeAutoObservable } from 'mobx';
import { ICreditReport, ICreditReportSummary, creditScoreRanges, CreditScoreLevel, ICreditReportSeats } from 'realhaus-sdk';
import { Collections } from './dbStore';
import FirebaseStore from './firebaseStore';
import { UserStore } from './userStore';
import { generateCreditReport, getApplicantCreditReport } from '../utils/apiClient';

export enum CreditScoreRange {
  EXCELLENT = 780,
  GOOD = 650,
  FAIR = 600,
  POOR = 500,
}

// How often watchCreditReport checks a report that is being generated, and for how long it waits
// after a request.
export const CREDIT_REPORT_POLL_MS = 5000;
export const CREDIT_REPORT_WAIT_MS = 15 * 60 * 1000;

const isInProgress = (report?: ICreditReport) =>
  (report as { status?: string } | undefined)?.status === 'IN_PROGRESS';

export class CreditReportStore {

  constructor(private fireStore: FirebaseStore, private userStore: UserStore) {
    makeAutoObservable(this)
  }

  /**
   * The credit report of a rent application's applicant. Only the landlord of the application's
   * listing can get it: credit reports are readable only by the person they are about, so it
   * comes from the server.
   */
  getCreditReport = async (rentApplicationId: string): Promise<ICreditReport | undefined> => {
    return (await this.fetchCreditReport(rentApplicationId)) ?? undefined;
  }

  /** null when the applicant has no report, undefined when the request failed. */
  private fetchCreditReport = async (rentApplicationId: string): Promise<ICreditReport | null | undefined> => {
    if (!rentApplicationId) return;

    const token = await this.userStore.getUserToken() || ''
    return getApplicantCreditReport({ token, body: { rentApplicationId } });
  }

  getCreditReportSummary = async (rentApplicationId: string): Promise<ICreditReportSummary | undefined> => {
    const creditReport = await this.getCreditReport(rentApplicationId);

    if (!creditReport) return;

    const creditScore = creditReport.creditScore;

    const creditScoreRange = creditScoreRanges.find(({ upper, lower }) => creditScore < upper && creditScore > lower)
      || creditScoreRanges.find(({ level }) => CreditScoreLevel.POOR === level)

    const numberOfPublicRecords = creditReport.publicRecords ?
      (creditReport.publicRecords.bankruptcies ? creditReport.publicRecords.bankruptcies.length : 0)
      + (creditReport.publicRecords.collections ? creditReport.publicRecords.collections.length : 0)
      + (creditReport.publicRecords.judgement ? creditReport.publicRecords.judgement.length : 0)
      : 0;

    return {
      creditScore,
      creditScoreRange,
      criminalHistory: creditReport.publicRecords && creditReport.publicRecords.judgement ? creditReport.publicRecords.judgement.length : 0,
      evictions: creditReport.evictions ? creditReport.evictions.length : 0,
      employers: creditReport.personalInformation && creditReport.personalInformation.employment ? creditReport.personalInformation.employment.length : 0,
      collections: creditReport.publicRecords && creditReport.publicRecords.collections ? creditReport.publicRecords.collections.length : 0,
      publicRecords: numberOfPublicRecords,
    } as ICreditReportSummary
  }

  getCreditReportSeats = async (userId: string): Promise<ICreditReportSeats | undefined> => {
    if (!userId) return

    const documentRef = await this.fireStore.getDocument(Collections.creditReportSeats, userId);

    if (!documentRef.exists()) {
      return;
    }

    const reportSeatsData = documentRef.data() as ICreditReportSeats

    return reportSeatsData
  }

  generateCreditReport = async (rentApplicationId: string) => {
    if (!rentApplicationId) return;

    const uid = this.fireStore.authService.currentUser?.uid

    if (!uid) return;

    const token = await this.userStore.getUserToken() || ''

    const req = {
      token, body: { rentApplicationId }
    }

    return await generateCreditReport(req);
  }

  subscribeToCreditReportSeats = (userId: string, callback: (data?: ICreditReportSeats) => void) => {
    return this.fireStore.subscribeDocument(Collections.creditReportSeats, userId, (d) => { callback(d.data() as ICreditReportSeats) });
  }

  /**
   * Calls back with the application's credit report, then checks again every
   * CREDIT_REPORT_POLL_MS while it is being generated. The report comes from the server, so it
   * can't be subscribed to.
   *
   * Pass afterRequest right after requesting a report: the report is only marked IN_PROGRESS a
   * few seconds after the request, so until then the old report (or none) is not passed on.
   * Waits at most CREDIT_REPORT_WAIT_MS. A failed check is not passed on, and is retried only while
   * waiting.
   * Returns a function that stops checking.
   */
  watchCreditReport = (
    rentApplicationId: string,
    callback: (data?: ICreditReport) => void,
    afterRequest = false,
    pollMs = CREDIT_REPORT_POLL_MS,
  ) => {
    let stopped = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let seenInProgress = !afterRequest;
    let inProgress = false;
    const deadline = Date.now() + CREDIT_REPORT_WAIT_MS;

    const check = async () => {
      const result = await this.fetchCreditReport(rentApplicationId);
      if (stopped) return;

      if (result !== undefined) {
        const report = result ?? undefined;
        inProgress = isInProgress(report);
        seenInProgress = seenInProgress || inProgress;
        if (seenInProgress || Date.now() >= deadline) callback(report);
      }

      const waiting = inProgress || !seenInProgress;
      if (waiting && Date.now() < deadline) timer = setTimeout(check, pollMs);
    };
    check();

    return () => {
      stopped = true;
      clearTimeout(timer);
    };
  }
}